import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Signal } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATE_WORDS = {
  EARLY: "early", DEVELOPING: "developing", NEAR_TRIGGER: "close to its trigger",
  TRIGGERED: "triggered", INVALIDATED: "no longer valid",
};
const SMALL = new Set(["or", "and", "the", "of", "a", "an", "to", "in", "on", "vs"]);
const titleCase = (s) => s.toLowerCase().split(" ")
  .map((w, i) => (i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
const plainState = (s) => STATE_WORDS[s.trim()] || s.trim().toLowerCase().replace(/_developing$/, " forming").replace(/_/g, " ");

export function plainChange(it) {
  const sym = it.symbol;
  if (it.type === "INVALIDATED") return `${sym}: a setup TRADECRAFT was tracking no longer holds.`;
  if (it.type === "NEW") {
    const kind = (it.text || "").split("·")[1];
    return kind ? `${sym} is new on your radar: a ${plainState(kind)}.` : `${sym} is new on your radar.`;
  }
  const mat = (it.detail || []).find((x) => /Condition maturity:.*→/.test(x));
  const arrow = (mat ? mat.replace(/^.*Condition maturity:/, "") : it.text || "").split("→");
  if (arrow.length === 2) return `${sym}'s setup moved from ${plainState(arrow[0])} to ${plainState(arrow[1])}.`;
  const rr = (it.detail || []).map((x) => x.match(/Risk\/reward changed from ([\d.]+R) to ([\d.]+R)/)).find(Boolean);
  if (rr) return `${sym}'s structure changed: reward vs risk is now ${rr[2]} (was ${rr[1]}).`;
  return `${sym}'s structure changed in a way that matters.`;
}

export function missionItem(sum, assess) {
  const c = sum?.continue;
  if (!c || sum.all_complete) return null;
  const fresh = c.first_ever && !assess?.assessed && !assess?.taken_at && !assess?.reset_at;
  if (fresh) return { id: "assess", text: "Find your starting point: a short skills check so you can skip what you already know", cta: "Begin", to: "/assess" };
  const mins = parseInt(c.est, 10);
  const label = c.is_boss ? titleCase(c.name) : `Mission ${c.number}: ${titleCase(c.name)}`;
  const verb = c.first_ever ? "Start" : "Continue";
  return { id: "mission", text: `${verb} ${label}${mins ? ` · about ${mins} minutes` : ""}`, cta: "Start", to: `/training/${c.id}`,
    label: `${verb} ${c.is_boss ? titleCase(c.name) : `Mission ${c.number}`}`, why: c.tagline };
}

// Next Move mirrors Signal item 1 so the two never disagree.
export function nextMoveFrom(sum, assess) {
  const m = missionItem(sum, assess);
  if (!m) return null;
  if (m.id === "assess") return { label: "Find your starting point", to: m.to, kind: "assessment", why: "A short skills check so you can skip what you already know." };
  return { label: m.label, to: m.to, kind: "mission", why: m.why };
}

function skillItem(d) {
  const ev = (d.competency?.domains || []).filter((x) => x.level !== "NOT_ASSESSED");
  if (!ev.length) return null;
  const weak = ev.reduce((a, b) => (b.mastery < a.mastery ? b : a));
  const opp = d.authorization?.INTELLIGENCE && d.brief?.your_development_opportunity;
  const sub = opp && opp.domain === weak.domain && opp.symbol ? `A live example is on ${opp.symbol} right now.` : null;
  return { id: "skill", text: `${weak.domain} is your weakest skill right now`, sub, cta: "Practice", to: "/replay" };
}

const ORDER = { INVALIDATED: 0, MATERIAL: 1, NEW: 2 };
const specific = (i) => !/in a way that matters|new on your radar\.$/.test(plainChange(i));
const rank = (i) => (ORDER[i.type] ?? 3) * 2 + (specific(i) ? 0 : 1);
function attentionItem(d, streak, fam) {
  if (d.authorization?.INTELLIGENCE) {
    const items = (d.changes?.items || []).filter((i) => i.material !== false);
    const top = [...items].sort((a, b) => rank(a) - rank(b))[0];
    if (top) return { id: "market", text: plainChange(top), cta: "Look", to: "/intelligence" };
  }
  if (fam?.goal) {
    const left = fam.days_left != null ? ` · ${fam.days_left} day${fam.days_left === 1 ? "" : "s"} left` : "";
    return { id: "family", text: `Family Week: ${fam.goal.value} of ${fam.goal.target} toward "${fam.goal.label}"${left}`, cta: "View", to: "/family/week" };
  }
  if (streak > 0) return { id: "streak", text: `Day ${streak} of your streak: one mission keeps it alive`, cta: "Keep it", to: "/training" };
  return null;
}

export default function TodaysSignal({ d, sum, assess }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fam, setFam] = useState(null);
  const isParent = (user?.student_ids?.length || 0) > 0;
  const needFam = isParent && !d.authorization?.INTELLIGENCE;
  const [ready, setReady] = useState(!needFam);

  useEffect(() => {
    if (needFam) api.get("/family-week").then((r) => setFam(r.data)).catch(() => {}).finally(() => setReady(true));
  }, [needFam]);

  if (!ready) return null;
  const items = [missionItem(sum, assess), skillItem(d), attentionItem(d, sum?.streak || 0, fam)].filter(Boolean);
  if (!items.length) return null;

  return (
    <section className="tc-card p-5 animate-fade-up" data-testid="todays-signal">
      <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5">
        <Signal className="w-3.5 h-3.5" /> Today's Signal
      </h2>
      <ol className="mt-3 divide-y divide-white/5">
        {items.map((it, i) => (
          <li key={it.id} className="flex items-center gap-4 py-3" data-testid={`signal-item-${it.id}`}>
            <span className="font-mono text-sm text-tc-coach w-5 shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-tc-txt font-medium" data-testid={`signal-text-${it.id}`}>{it.text}</div>
              {it.sub && <div className="text-[12px] text-tc-muted mt-0.5" data-testid={`signal-sub-${it.id}`}>{it.sub}</div>}
            </div>
            <button data-testid={`signal-btn-${it.id}`} onClick={() => navigate(it.to)}
              className="shrink-0 rounded-lg bg-tc-coach/15 border border-tc-coach/30 text-tc-coach font-semibold px-4 py-2 text-sm hover:bg-tc-coach hover:text-tc-bg transition-colors flex items-center gap-1.5">
              {it.cta} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
